"use client";

import { LocaleLink } from "@i18n/routing";
import { Button } from "@ui/components/button";
import { useTranslations } from "next-intl";
import { useEffect, useMemo } from "react";
import { canAutoReload, markAutoReload } from "../lib/auto-reload";
import { reportClientError } from "../lib/report-client-error";

/**
 * Shared body for the route-level error.tsx boundaries. Rendered inside the
 * layout (and inside NextIntlClientProvider), so the chrome and the user's
 * language survive the crash.
 */
export function ErrorPage({
	error,
	reset,
}: {
	error: Error & { digest?: string };
	reset: () => void;
}) {
	const t = useTranslations("shared.errorPage");
	// 和 global-error 同一套：代码块加载失败 / RSC 流断掉，静默刷新一次就好，
	// 不先闪错误页（2026-10-29：14 天 184 次落在这里，90 次在 /ai-edit-image）
	const reloading = useMemo(() => canAutoReload(error), [error]);

	useEffect(() => {
		if (reloading) {
			markAutoReload();
		}
		// 先上报：要刷新时走 beacon，保证事件发得出去
		reportClientError("boundary", error, {
			digest: error.digest ?? "",
			reloading,
		});
		if (reloading) {
			window.location.reload();
		}
	}, [error, reloading]);

	if (reloading) {
		return <div className="min-h-[50vh]" />;
	}

	return (
		<div className="flex min-h-[50vh] flex-col items-center justify-center px-4 text-center">
			<h1 className="text-2xl font-bold text-foreground">{t("title")}</h1>
			<p className="mt-2 max-w-md text-foreground/60">
				{t("description")}
			</p>

			<div className="mt-6 flex flex-wrap justify-center gap-3">
				<Button type="button" onClick={reset}>
					{t("retry")}
				</Button>
				<Button type="button" variant="outline" asChild>
					<LocaleLink href="/">{t("home")}</LocaleLink>
				</Button>
			</div>

			{error.digest && (
				<p className="mt-6 text-xs text-foreground/40">
					{t("reference", { digest: error.digest })}
				</p>
			)}
		</div>
	);
}
